import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { DropdownMenu, DropdownMenuItem } from '@repo/design-system/components/ui/dropdown-menu';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CompletionFilter } from './checklist-utils';

const SEARCH_DEBOUNCE_MS = 400;

interface SearchFiltersProps {
    searchQuery: string;
    onSearchChange: (value: string) => void;
    completionFilter?: CompletionFilter;
    onCompletionFilterChange?: (value: CompletionFilter) => void;
    inputRef?: React.Ref<HTMLInputElement>;
    onFocus?: () => void;
}

const searchWrapperStyles = css({
    flex: '1',
    minWidth: '0',
    borderRadius: 'full',
    overflow: 'hidden',
    backdropFilter: '[blur(32px)]',
});

export const SearchFilters = ({
    searchQuery,
    onSearchChange,
    completionFilter,
    onCompletionFilterChange,
    inputRef,
    onFocus,
}: SearchFiltersProps): React.JSX.Element => {
    const { t } = useT(['game_map']);
    const [inputValue, setInputValue] = useState(searchQuery);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setInputValue(searchQuery);
    }, [searchQuery]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setInputValue(value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                debounceRef.current = null;
                onSearchChange(value);
            }, SEARCH_DEBOUNCE_MS);
        },
        [onSearchChange],
    );

    useEffect(
        () => () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        },
        [],
    );

    const showFilter = completionFilter != null && onCompletionFilterChange != null;

    return (
        <FlexRow width="full" gap="8" align="center">
            <div className={searchWrapperStyles}>
                <TextInput
                    ref={inputRef}
                    placeholder={t($ => $.checklist_pages.search_locations_placeholder)}
                    value={inputValue}
                    onChange={e => handleSearchChange(e.target.value)}
                    onFocus={onFocus}
                    leadingIcon="search"
                    fullWidth
                    size={showFilter ? 'sm' : 'md'}
                    borderRadius="full"
                    aria-label={t($ => $.checklist_pages.search_checklist_items_aria_label)}
                />
            </div>

            {showFilter && (
                <DropdownMenu
                    placement="bottom-end"
                    onSelect={details => onCompletionFilterChange(details.value as CompletionFilter)}
                    trigger={
                        <IconButton
                            variant="filled"
                            color="secondary"
                            size="md"
                            aria-label={t($ => $.checklist_pages.filter_by_completion_status_aria_label)}
                        >
                            <Icon icon="filter_list" decorative />
                        </IconButton>
                    }
                >
                    <DropdownMenuItem value="all" trailingIcon={completionFilter === 'all' ? 'check' : undefined}>
                        {t($ => $.checklist_pages.show_all)}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        value="completed"
                        trailingIcon={completionFilter === 'completed' ? 'check' : undefined}
                    >
                        {t($ => $.checklist_pages.completed_filter)}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        value="uncompleted"
                        trailingIcon={completionFilter === 'uncompleted' ? 'check' : undefined}
                    >
                        {t($ => $.checklist_pages.uncompleted_filter)}
                    </DropdownMenuItem>
                </DropdownMenu>
            )}
        </FlexRow>
    );
};
